"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined";
import SalesIcon from "@mui/icons-material/PointOfSaleOutlined";
import PeopleIcon from "@mui/icons-material/PeopleAltOutlined";
import WorkIcon from "@mui/icons-material/WorkOutlineOutlined";
import ChartIcon from "@mui/icons-material/InsightsOutlined";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import SearchIcon from "@mui/icons-material/SearchRounded";
import AddIcon from "@mui/icons-material/AddRounded";
import MenuIcon from "@mui/icons-material/MenuRounded";
import CollapseIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import ExpandIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import CloudDoneIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffIcon from "@mui/icons-material/CloudOffOutlined";
import CloudSyncIcon from "@mui/icons-material/CloudSyncOutlined";
import ChevronDown from "@mui/icons-material/KeyboardArrowDownRounded";
import LockIcon from "@mui/icons-material/LockOutlined";
import { InvoiceStoreProvider, useInvoiceStore } from "@/lib/invoice/store";
import { computeTotals, fmtMoney, invoiceStatus, daysBetween, todayISO } from "@/lib/invoice/utils";
import { ErrorBoundary, FeedbackProvider, StatusBadge, cx } from "./ui";
import { DriveSyncProvider, useDrive } from "./DriveSync";
import { AskAIButton } from "./AskAI";
import { passwordProblems } from "@/lib/invoice/crypto";
import { applyTheme } from "@/lib/invoice/theme";
import { DEFAULT_APP_NAME, rememberAppName, resolveAppName } from "@/lib/invoice/appName";
import LockOpenIcon from "@mui/icons-material/LockOpenOutlined";

const SKIP_LOGIN_KEY = "invoice-studio-login-setup-skipped";

const NAV = [
  {
    section: "Workspace",
    items: [
      { href: "/invoice", label: "Dashboard", icon: DashboardIcon },
      { href: "/invoice/invoices", label: "Invoices", icon: ReceiptIcon, badge: "overdue" },
      { href: "/invoice/sales", label: "Sales", icon: SalesIcon, badge: "unbilled" },
      { href: "/invoice/clients", label: "Clients", icon: PeopleIcon },
      { href: "/invoice/projects", label: "Projects & services", icon: WorkIcon },
    ],
  },
  { section: "Insights", items: [{ href: "/invoice/reports", label: "Reports", icon: ChartIcon }] },
  {
    section: "Tools",
    items: [
      { href: "/invoice/import", label: "Import", icon: UploadIcon },
      { href: "/invoice/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
];
const ALL_NAV = NAV.flatMap((s) => s.items);
const COLLAPSE_KEY = "invoice-studio-sidebar-collapsed";

const clean = (p) => (p || "").replace(/\/+$/, "") || "/";
function isActive(path, href) {
  path = clean(path);
  if (href === "/invoice") return path === "/invoice";
  if (href === "/invoice/invoices") return path === href || path.startsWith("/invoice/invoices/view");
  return path.startsWith(href);
}

function useBadges() {
  const { invoices, sales } = useInvoiceStore();
  return useMemo(
    () => ({
      overdue: invoices.filter((i) => invoiceStatus(i) === "overdue").length,
      unbilled: (sales || []).filter((x) => !x.invoiceId).length,
    }),
    [invoices, sales]
  );
}

function BrandMark({ collapsed }) {
  const store = useInvoiceStore();
  const { defaultBrand } = store;
  const appName = resolveAppName(store);
  const initials = (store.settings?.appName ? appName : defaultBrand?.name || appName)
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <Link href="/invoice" className={cx("flex items-center gap-3 rounded-xl px-2 py-1.5", collapsed && "justify-center")}>
      {defaultBrand?.logo ? (
        <img src={defaultBrand.logo} alt="" className="h-9 w-9 shrink-0 rounded-lg bg-white object-contain p-0.5" />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-[13px] font-bold text-white shadow-lg shadow-brand-900/40">
          {initials}
        </div>
      )}
      {!collapsed && (
        <div className="min-w-0">
          <div className="truncate text-[14px] font-semibold tracking-tight text-white" data-testid="app-name-label">{appName}</div>
          <div className="truncate text-[11px] text-slate-400">{store.settings?.appTagline || defaultBrand?.name || "Billing workspace"}</div>
        </div>
      )}
    </Link>
  );
}

function NavItem({ item, active, collapsed, badge, onNavigate }) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      className={cx(
        "group relative flex items-center gap-3 rounded-lg px-2.5 py-2 text-[13.5px] font-medium transition-colors",
        collapsed && "justify-center px-0",
        active ? "bg-white/[0.08] text-white" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"
      )}
    >
      {active && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-x-2.5 -translate-y-1/2 rounded-r-full bg-brand-500" />}
      <Icon sx={{ fontSize: 19 }} className={active ? "text-brand-400" : "text-slate-500 group-hover:text-slate-300"} />
      {!collapsed && <span className="flex-1">{item.label}</span>}
      {badge > 0 &&
        (collapsed ? (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand-500 ring-2 ring-ink-900" />
        ) : (
          <span className={cx("rounded-full px-1.5 py-px text-[10px] font-semibold", item.badge === "overdue" ? "bg-brand-500 text-white" : "bg-white/10 text-slate-200")}>{badge}</span>
        ))}
      {collapsed && (
        <span className="pointer-events-none absolute left-full z-50 ml-3 hidden whitespace-nowrap rounded-md bg-ink-900 px-2 py-1 text-xs text-white shadow-pop ring-1 ring-white/10 group-hover:block">
          {item.label}
          {badge > 0 ? ` · ${badge}` : ""}
        </span>
      )}
    </Link>
  );
}

function Sidebar({ collapsed, onToggle, onNavigate, mobile }) {
  const path = usePathname();
  const badges = useBadges();
  return (
    <div className="sidebar-surface flex h-full flex-col bg-ink-900 text-slate-300">
      <div className={cx("flex items-center border-b border-white/5 px-3 py-3.5", collapsed ? "justify-center" : "justify-between")}>
        <BrandMark collapsed={collapsed} />
      </div>
      <nav className="nav-scroll flex-1 space-y-5 overflow-y-auto px-3 py-4">
        <Link
          href="/invoice/invoices/edit"
          onClick={onNavigate}
          title="New invoice"
          className={cx(
            "group relative flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-b from-brand-500 to-brand-700 py-2 text-[13px] font-semibold text-white shadow-lg shadow-brand-950/40 ring-1 ring-inset ring-white/10 transition hover:from-brand-400 hover:to-brand-600",
            collapsed && "px-0"
          )}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          {!collapsed && "New invoice"}
        </Link>
        {NAV.map((sec) => (
          <div key={sec.section}>
            {!collapsed ? (
              <div className="mb-1.5 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500">{sec.section}</div>
            ) : (
              <div className="mx-auto mb-2 h-px w-6 bg-white/10" />
            )}
            <div className="space-y-0.5">
              {sec.items.map((item) => (
                <NavItem key={item.href} item={item} active={isActive(path, item.href)} collapsed={collapsed} badge={item.badge ? badges[item.badge] : 0} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/5 p-3">
        {!collapsed && <StorageStatus />}
        {!mobile && (
          <button
            onClick={onToggle}
            title={collapsed ? "Expand sidebar (Ctrl+B)" : "Collapse sidebar (Ctrl+B)"}
            className={cx(
              "mt-2 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-[12.5px] font-medium text-slate-400 transition hover:bg-white/[0.05] hover:text-slate-100",
              collapsed && "justify-center px-0"
            )}
          >
            {collapsed ? <ExpandIcon sx={{ fontSize: 18 }} /> : <CollapseIcon sx={{ fontSize: 18 }} />}
            {!collapsed && (
              <>
                <span className="flex-1 text-left">Collapse</span>
                <kbd className="rounded border border-white/10 px-1 text-[10px] text-slate-500">Ctrl B</kbd>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function StorageStatus() {
  const { backup, persisted, ready } = useInvoiceStore();
  const drive = useDrive();
  if (!ready) return null;
  const dLabel = { off: "Drive sync off", "needs-auth": "Drive: sign in to sync", syncing: "Drive: syncing…", synced: drive.cfg?.dirty ? "Drive: saving…" : "Drive: synced", conflict: "Drive: needs your choice", error: "Drive: error" }[drive.status];
  const dot = (ok, warn) => cx("mr-2 inline-block h-1.5 w-1.5 rounded-full", ok ? "bg-emerald-400" : warn ? "bg-amber-400" : "bg-slate-600");
  return (
    <div className="space-y-1 px-2.5 text-[11px] leading-snug text-slate-500">
      <div>
        <span className={dot(persisted, !persisted)} />
        {persisted ? "Saved on this device" : "Saved on this device (unprotected)"}
      </div>
      <div>
        <span className={dot(drive.status === "synced", drive.status !== "off")} />
        {dLabel}
      </div>
      {backup.linked && (
        <div>
          <span className={dot(backup.granted, !backup.granted)} />
          {backup.granted ? "Auto-backup file on" : "Auto-backup paused"}
        </div>
      )}
      <div className="pt-1 text-slate-600" data-testid="app-version" title="Build of this app – compare after an update">
        Version {process.env.NEXT_PUBLIC_BUILD_ID || "local"}
      </div>
    </div>
  );
}

function SyncPill() {
  const drive = useDrive();
  const store = useInvoiceStore();
  if (!store.ready) return null;
  if (!drive.cfg?.enabled)
    return (
      <Link
        href="/invoice/settings?tab=data"
        className="hidden items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 sm:inline-flex"
        title="Your data is only on this device – connect Google Drive"
        data-testid="sync-pill"
      >
        <CloudOffIcon sx={{ fontSize: 15 }} />
        Not synced
      </Link>
    );
  const map = {
    synced: [drive.cfg?.dirty ? "Saving…" : "Synced", CloudDoneIcon, "text-emerald-700 bg-emerald-50 ring-emerald-600/15"],
    syncing: ["Syncing…", CloudSyncIcon, "text-slate-600 bg-slate-100 ring-slate-500/10"],
    "needs-auth": [drive.renewFailed ? "Drive not syncing" : "Reconnecting…", drive.renewFailed ? CloudOffIcon : CloudSyncIcon, drive.renewFailed ? "text-red-700 bg-red-50 ring-red-600/20" : "text-amber-800 bg-amber-50 ring-amber-600/20"],
    conflict: ["Sync paused", CloudOffIcon, "text-red-700 bg-red-50 ring-red-600/20"],
    error: ["Sync error", CloudOffIcon, "text-red-700 bg-red-50 ring-red-600/15"],
  };
  let [label, Icon, cls] = map[drive.status] || map.syncing;
  if (drive.offline) [label, Icon, cls] = ["Offline", CloudOffIcon, "text-amber-800 bg-amber-50 ring-amber-600/20"];
  const last = drive.cfg?.lastSyncAt ? new Date(drive.cfg.lastSyncAt).toLocaleString() : "never";
  return (
    <button
      onClick={() => (drive.status === "needs-auth" ? drive.reconnect() : drive.syncNow())}
      className={cx("hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset sm:inline-flex", cls)}
      title={`Google Drive sync · last synced ${last}`}
      data-testid="sync-pill"
    >
      <Icon sx={{ fontSize: 15 }} />
      {label}
    </button>
  );
}

function NewMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const items = [
    ["Invoice", "/invoice/invoices/edit", "Tax / export invoice"],
    ["Record a sale", "/invoice/sales", "Log work to bill at month end"],
    ["Quotation", "/invoice/invoices/edit?type=quote", "Estimate for a client"],
    ["Client", "/invoice/clients?new=1", "Add a customer"],
    ["Project", "/invoice/projects?new=1", "Retainer or one-off job"],
  ];
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-9 items-center gap-1 rounded-lg bg-ink-900 pl-3 pr-2 text-sm font-medium text-white shadow-xs transition hover:bg-ink-700"
      >
        <AddIcon sx={{ fontSize: 18 }} />
        New
        <ChevronDown sx={{ fontSize: 18 }} className="opacity-70" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-64 animate-pop-in overflow-hidden rounded-xl bg-white p-1.5 shadow-pop ring-1 ring-slate-900/5">
          {items.map(([l, href, d]) => (
            <Link key={l} href={href} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 hover:bg-slate-50">
              <div className="text-sm font-medium text-slate-900">{l}</div>
              <div className="text-xs text-slate-500">{d}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function UserChip() {
  const store = useInvoiceStore();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  if (!store.loginEnabled)
    return (
      <Link
        href="/invoice/settings?tab=security"
        title="Login is off – set up a user ID and password"
        className="flex h-9 items-center gap-1.5 rounded-full bg-amber-50 px-3 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20 hover:bg-amber-100"
      >
        <LockOpenIcon sx={{ fontSize: 16 }} />
        <span className="hidden sm:inline">Set up login</span>
      </Link>
    );
  const name = store.currentUser || "user";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={`Signed in as ${name}`}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-slate-700 to-ink-900 text-xs font-semibold uppercase text-white ring-2 ring-white"
      >
        {name.slice(0, 2)}
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 animate-pop-in rounded-xl bg-white p-1.5 shadow-pop ring-1 ring-slate-900/5">
          <div className="px-3 py-2 text-xs text-slate-500">
            Signed in as <b className="text-slate-800">{name}</b>
          </div>
          <Link href="/invoice/settings?tab=security" onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-slate-50">
            Security & users
          </Link>
          <button onClick={() => store.lock()} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50">
            <LockIcon sx={{ fontSize: 16 }} /> Lock / sign out
          </button>
        </div>
      )}
    </div>
  );
}

function LoginScreen() {
  const store = useInvoiceStore();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [remember, setRemember] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);
  const company = store.auth?.company || "";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await store.unlock(u, p, remember);
    } catch (x) {
      setErr(x.message === "locked" ? "Couldn’t open the data with this login." : x.message);
      setP("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink-900 p-4 font-sans">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand-600/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[480px] w-[480px] rounded-full bg-[#2a78d6]/15 blur-3xl" />
      <div className="relative w-full max-w-sm animate-pop-in">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/40">
            <LockIcon />
          </div>
          <div className="text-[22px] font-semibold tracking-tight text-white">{resolveAppName(store)}</div>
          <div className="mt-1 text-sm text-slate-400">{company ? `${company} · ` : ""}sign in to continue</div>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white p-6 shadow-pop">
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">User ID</span>
            <input autoFocus autoComplete="username" value={u} onChange={(e) => setU(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm shadow-xs focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">Password</span>
            <input type="password" autoComplete="current-password" value={p} onChange={(e) => setP(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm shadow-xs focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            Remember this device
          </label>
          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
          <button
            type="submit"
            disabled={busy || !u || !p}
            className="flex h-11 w-full items-center justify-center rounded-lg bg-gradient-to-b from-brand-600 to-brand-700 text-sm font-semibold text-white shadow-xs ring-1 ring-inset ring-black/10 transition hover:from-brand-500 hover:to-brand-600 disabled:opacity-60"
          >
            {busy ? "Unlocking…" : "Sign in"}
          </button>
          <button type="button" onClick={() => setHelp((h) => !h)} className="w-full text-center text-xs text-slate-500 hover:text-slate-700">
            Forgot password?
          </button>
          {help && (
            <div className="rounded-lg bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
              Your data is encrypted with your password, so it can’t be reset by email. Ask another user of this workspace to sign in and create a new login for you
              (Settings → Security), or restore an older backup whose password you remember.
            </div>
          )}
        </form>
        <div className="mt-4 text-center text-[11px] text-slate-500">Data is encrypted on this device with AES-256 · nothing is sent to any server</div>
      </div>
    </div>
  );
}

// Shows the sign-in screen while locked and locks again after inactivity.
function Gate({ children }) {
  const store = useInvoiceStore();
  const minutes = store.auth?.autoLockMinutes || 0;
  useEffect(() => {
    if (!minutes || store.locked || !store.loginEnabled) return;
    let t;
    const reset = () => {
      clearTimeout(t);
      t = setTimeout(() => store.lock(), minutes * 60000);
    };
    const evs = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(t);
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [minutes, store.locked, store.loginEnabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const appName = resolveAppName(store);
  useEffect(() => {
    if (store.ready && !store.locked) rememberAppName(String(store.settings?.appName || "").trim());
  }, [store.ready, store.locked, store.settings?.appName]);
  useEffect(() => {
    document.title = appName;
  }, [appName]);
  const [skipped, setSkipped] = useState(true);
  useEffect(() => {
    try {
      setSkipped(localStorage.getItem(SKIP_LOGIN_KEY) === "1");
    } catch {
      setSkipped(false);
    }
  }, []);
  if (store.locked) return <LoginScreen />;
  if (store.ready && !store.loginEnabled && !skipped)
    return (
      <SetupLogin
        onSkip={() => {
          try {
            localStorage.setItem(SKIP_LOGIN_KEY, "1");
          } catch {
            /* ignore */
          }
          setSkipped(true);
        }}
      />
    );
  return children;
}

// First run: create the sign-in (and optionally name the tool) – or skip.
function SetupLogin({ onSkip }) {
  const store = useInvoiceStore();
  const [f, setF] = useState({ app: store.settings?.appName || "", u: "", p: "", c: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const issue = f.p ? passwordProblems(f.p) : "";
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.u.trim()) return setErr("Choose a user ID");
    if (issue) return setErr(issue);
    if (f.p !== f.c) return setErr("Passwords don’t match");
    setBusy(true);
    try {
      if (f.app.trim() !== (store.settings?.appName || "")) store.updateSettings({ appName: f.app.trim() });
      await store.enableLogin(f.u.trim(), f.p);
    } catch (x) {
      setErr(x.message || String(x));
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm shadow-xs focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10";
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink-900 p-4 font-sans">
      <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-brand-600/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 -right-32 h-[480px] w-[480px] rounded-full bg-[#2a78d6]/15 blur-3xl" />
      <div className="relative w-full max-w-md animate-pop-in">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/40">
            <LockIcon />
          </div>
          <div className="text-[22px] font-semibold tracking-tight text-white">Secure your workspace</div>
          <div className="mt-1 text-sm text-slate-400">Create the user ID and password you’ll sign in with</div>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white p-6 shadow-pop" data-testid="setup-login">
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">Name of this tool (optional)</span>
            <input value={f.app} onChange={set("app")} placeholder={DEFAULT_APP_NAME} maxLength={40} className={input} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">User ID</span>
            <input autoFocus autoComplete="username" value={f.u} onChange={set("u")} className={input} />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">Password</span>
            <input type="password" autoComplete="new-password" value={f.p} onChange={set("p")} className={input} />
            <span className={cx("mt-1 block text-[11px]", issue ? "text-amber-700" : "text-slate-500")}>{issue || "At least 8 characters, mixing letters with numbers or symbols."}</span>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-slate-600">Confirm password</span>
            <input type="password" autoComplete="new-password" value={f.c} onChange={set("c")} className={input} />
          </label>
          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
          <button
            type="submit"
            disabled={busy}
            className="flex h-11 w-full items-center justify-center rounded-lg bg-gradient-to-b from-brand-600 to-brand-700 text-sm font-semibold text-white shadow-xs ring-1 ring-inset ring-black/10 transition hover:from-brand-500 hover:to-brand-600 disabled:opacity-60"
          >
            {busy ? "Encrypting your data…" : "Create login"}
          </button>
          <div className="rounded-lg bg-slate-50 p-3 text-[11px] leading-relaxed text-slate-500">
            There’s no password reset – your data is encrypted with this password. Write it down, or add a second user later in Settings → Security.
          </div>
        </form>
        <button onClick={onSkip} className="mx-auto mt-4 block text-sm text-slate-400 underline-offset-4 hover:text-white hover:underline">
          Skip for now – use without a login
        </button>
      </div>
    </div>
  );
}

function TopBar({ onMenu, onSearch }) {
  const path = usePathname();
  const appName = resolveAppName(useInvoiceStore());
  const current = ALL_NAV.find((n) => isActive(path, n.href)) || (clean(path).startsWith("/invoice/invoices") ? ALL_NAV.find((n) => n.href === "/invoice/invoices") : null);
  const sub = clean(path).startsWith("/invoice/invoices/edit")
    ? "Editor"
    : clean(path).startsWith("/invoice/invoices/view")
    ? "Invoice"
    : clean(path).startsWith("/invoice/clients/statement")
    ? "Statement"
    : "";
  return (
    <header className="topbar-surface sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200/70 bg-white/75 px-4 backdrop-blur-xl sm:px-6 print:hidden">
      <button className="rounded-lg p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={onMenu} aria-label="Open menu">
        <MenuIcon />
      </button>
      <nav className="flex min-w-0 items-center gap-1.5 text-sm">
        <span className="hidden text-slate-400 sm:inline">{appName}</span>
        <span className="hidden text-slate-300 sm:inline">/</span>
        <span className="truncate font-medium text-slate-800">{current?.label || appName}</span>
        {sub && (
          <>
            <span className="text-slate-300">/</span>
            <span className="truncate text-slate-500">{sub}</span>
          </>
        )}
      </nav>
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onSearch}
          className="flex h-9 items-center gap-2 rounded-lg bg-slate-100/80 px-3 text-sm text-slate-500 ring-1 ring-inset ring-slate-200/70 transition hover:bg-slate-100 hover:text-slate-700 sm:w-64"
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          <span className="hidden flex-1 text-left sm:inline">Search or jump to…</span>
          <kbd className="hidden rounded border border-slate-300 bg-white px-1.5 text-[10px] font-medium text-slate-500 sm:inline">Ctrl K</kbd>
        </button>
        <SyncPill />
        <NewMenu />
        <AskAIButton />
        <UserChip />
      </div>
    </header>
  );
}

const DRIVE_NAG_KEY = "invoice-studio-drive-nag-until";

function Bar({ tone, children }) {
  const cls = {
    red: "border-red-200 bg-red-50 text-red-900",
    amber: "border-amber-200/80 bg-amber-50/80 text-amber-900",
  }[tone];
  return <div className={cx("flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-4 py-2.5 text-sm", cls)}>{children}</div>;
}

function Banners() {
  const store = useInvoiceStore();
  const drive = useDrive();
  const [nagHidden, setNagHidden] = useState(true);
  useEffect(() => {
    try {
      setNagHidden(Number(localStorage.getItem(DRIVE_NAG_KEY) || 0) > Date.now());
    } catch {
      setNagHidden(false);
    }
  }, []);
  if (!store.ready) return null;
  const items = [];
  const hasData = store.invoices.length + store.clients.length + store.projects.length > 0;
  const unsynced = drive.cfg?.dirty ? " This device has changes that aren’t in Drive yet." : "";

  if (drive.cfg?.enabled) {
    if (drive.offline)
      items.push(
        <Bar key="off" tone="amber">
          <CloudOffIcon sx={{ fontSize: 18 }} />
          <span>You’re offline. Everything is saved on this device and will sync to Google Drive as soon as you’re back online.</span>
        </Bar>
      );
    else if (drive.status === "needs-auth")
      items.push(
        drive.renewFailed ? (
          <Bar key="na" tone="red">
            <CloudOffIcon sx={{ fontSize: 18 }} />
            <span>
              <b>Google Drive couldn’t reconnect.</b> Allow pop-ups for this site, then press Reconnect.{unsynced}
            </span>
            <button className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700" onClick={() => drive.reconnect()}>
              Reconnect
            </button>
          </Bar>
        ) : (
          <Bar key="na" tone="amber">
            <CloudSyncIcon sx={{ fontSize: 18 }} />
            <span>
              <b>Reconnecting to Google Drive</b> – Google asks for a click after an hour, so it will reconnect on your next click anywhere.{unsynced}
            </span>
            <button className="rounded-lg bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700" onClick={() => drive.reconnect()}>
              Reconnect now
            </button>
          </Bar>
        )
      );
    else if (drive.status === "error")
      items.push(
        <Bar key="er" tone="red">
          <CloudOffIcon sx={{ fontSize: 18 }} />
          <span>
            <b>Google Drive sync failed:</b> {drive.error}.{unsynced}
          </span>
          <button className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700" onClick={() => drive.syncNow()}>
            Try again
          </button>
        </Bar>
      );
    else if (drive.status === "conflict")
      items.push(
        <Bar key="cf" tone="red">
          <CloudOffIcon sx={{ fontSize: 18 }} />
          <span>
            <b>Sync paused:</b> this device and Google Drive both have changes. Choose which to keep to continue syncing.
          </span>
          <button className="rounded-lg bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700" onClick={() => drive.syncNow()}>
            Review
          </button>
        </Bar>
      );
  } else if (hasData && !nagHidden) {
    items.push(
      <Bar key="nd" tone="amber">
        <CloudOffIcon sx={{ fontSize: 18 }} />
        <span>
          <b>Not synced to Google Drive.</b> Your data is only on this device – if this browser’s data is cleared it’s lost, and your other devices can’t see it.
        </span>
        <Link className="rounded-lg bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700" href="/invoice/settings?tab=data">
          Connect Google Drive
        </Link>
        <button
          className="text-xs text-amber-800 underline-offset-2 hover:underline"
          onClick={() => {
            try {
              localStorage.setItem(DRIVE_NAG_KEY, String(Date.now() + 3 * 864e5));
            } catch {
              /* ignore */
            }
            setNagHidden(true);
          }}
        >
          Remind me in 3 days
        </button>
      </Bar>
    );
  }
  if (store.saveError) items.push(<Bar key="e" tone="red">{store.saveError}</Bar>);
  if (store.backup.linked && !store.backup.granted) {
    items.push(
      <Bar key="b" tone="amber">
        <span>
          Auto-backup to <b>{store.backup.name}</b> is paused.{" "}
          <button className="font-semibold text-brand-700 underline" onClick={() => store.reconnectBackup()}>
            Resume auto-backup
          </button>
        </span>
      </Bar>
    );
  } else if (!store.backup.linked && !drive.cfg?.enabled && nagHidden) {
    const last = store.settings.backup?.lastExportAt;
    const days = last ? daysBetween(last.slice(0, 10), todayISO()) : null;
    const remind = store.settings.backup?.remindDays || 7;
    if ((store.invoices.length > 0 && days == null) || days > remind) {
      items.push(
        <Bar key="r" tone="amber">
          <span>
            {days == null ? "You haven’t backed up your data yet." : `Last backup was ${days} days ago.`}{" "}
            <Link className="font-semibold text-brand-700 underline" href="/invoice/settings?tab=data">
              Back up now
            </Link>{" "}
            or turn on Google Drive sync.
          </span>
        </Bar>
      );
    }
  }
  if (!items.length) return null;
  return <div className="mb-5 space-y-2 print:hidden" data-testid="banners">{items}</div>;
}

function CommandPalette({ open, onClose }) {
  const store = useInvoiceStore();
  const router = useRouter();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
    }
  }, [open]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const actions = [
      { label: "New invoice", hint: "Action", href: "/invoice/invoices/edit" },
      { label: "Record a sale", hint: "Action", href: "/invoice/sales" },
      { label: "New quotation", hint: "Action", href: "/invoice/invoices/edit?type=quote" },
      { label: "New client", hint: "Action", href: "/invoice/clients?new=1" },
      { label: "New project", hint: "Action", href: "/invoice/projects?new=1" },
      { label: "Month-end invoicing", hint: "Action", href: "/invoice/invoices?run=1" },
      { label: "Import past invoices", hint: "Action", href: "/invoice/import" },
      ...ALL_NAV.map((n) => ({ label: n.label, hint: "Go to", href: n.href })),
    ];
    const out = actions.filter((a) => !s || a.label.toLowerCase().includes(s));
    if (s) {
      for (const c of store.clients) {
        if ([c.name, c.email, c.gstin, c.contactPerson].join(" ").toLowerCase().includes(s)) out.push({ label: c.name, hint: "Client", href: `/invoice/clients?open=${c.id}` });
      }
      for (const i of store.invoices) {
        if ([i.number, i.billTo?.name, i.period].join(" ").toLowerCase().includes(s)) {
          const t = computeTotals(i);
          out.push({ label: `${i.number} · ${i.billTo?.name || ""}`, hint: fmtMoney(t.total, i.currency), status: invoiceStatus(i, t), href: `/invoice/invoices/view?id=${i.id}` });
        }
      }
      for (const p of store.projects) {
        if ([p.name, p.service].join(" ").toLowerCase().includes(s)) out.push({ label: [p.name, p.service].filter(Boolean).join(" "), hint: "Project", href: `/invoice/projects?client=${p.clientId}&q=${encodeURIComponent(p.name)}` });
      }
      for (const x of store.sales || []) {
        if ((x.description || "").toLowerCase().includes(s)) out.push({ label: x.description, hint: `Sale · ${x.date}`, href: `/invoice/sales?client=${x.clientId}` });
      }
    }
    return out.slice(0, 40);
  }, [q, store.clients, store.invoices, store.projects, store.sales]);

  if (!open) return null;
  const go = (r) => {
    onClose();
    router.push(r.href);
  };
  return (
    <div className="glass-portal fixed inset-0 z-50 flex animate-fade-in items-start justify-center bg-slate-950/40 p-4 pt-[12vh] backdrop-blur-[2px]" onMouseDown={onClose}>
      <div className="w-full max-w-xl animate-pop-in overflow-hidden rounded-2xl bg-white shadow-pop ring-1 ring-slate-900/5" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-slate-100 px-4">
          <SearchIcon className="text-slate-400" />
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setIdx(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setIdx((i) => Math.min(i + 1, results.length - 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setIdx((i) => Math.max(i - 1, 0));
              }
              if (e.key === "Enter" && results[idx]) go(results[idx]);
            }}
            placeholder="Search invoices, clients, projects, sales or type an action…"
            className="w-full py-4 text-[15px] outline-none"
          />
          <kbd className="rounded border border-slate-200 px-1.5 text-[10px] text-slate-400">Esc</kbd>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto p-1.5">
          {results.length === 0 && <li className="px-4 py-8 text-center text-sm text-slate-500">No matches</li>}
          {results.map((r, i) => (
            <li key={r.href + r.label + i}>
              <button
                onMouseEnter={() => setIdx(i)}
                onClick={() => go(r)}
                className={cx("flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm", i === idx ? "bg-slate-100" : "")}
              >
                <span className="truncate text-slate-800">{r.label}</span>
                <span className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                  {r.status && <StatusBadge status={r.status} />}
                  {r.hint}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function InvoiceShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const path = usePathname();

  useEffect(() => {
    try {
      setCollapsed(localStorage.getItem(COLLAPSE_KEY) === "1");
    } catch {
      /* ignore */
    }
  }, []);
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !c;
    });

  useEffect(() => {
    setMobileOpen(false);
  }, [path]);

  // glass / classic look – only while the app is open (the landing page has its own)
  useEffect(() => {
    applyTheme();
    return () => delete document.documentElement.dataset.theme;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "k") {
        e.preventDefault();
        setPalette((p) => !p);
      }
      if ((e.ctrlKey || e.metaKey) && k === "b") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <ErrorBoundary resetKey={path}>
    <InvoiceStoreProvider>
      <FeedbackProvider>
        <Gate>
        <DriveSyncProvider>
          <div className="app-shell min-h-screen bg-[#f6f7f9] font-sans text-slate-900 print:bg-white">
            <div className="glass-backdrop print:hidden" aria-hidden />
            {/* desktop sidebar */}
            <aside
              className={cx(
                "fixed inset-y-0 left-0 z-40 hidden border-r border-black/20 transition-[width] duration-200 ease-out lg:block print:hidden",
                collapsed ? "w-[72px]" : "w-64"
              )}
            >
              <ErrorBoundary resetKey={path}>
                <Sidebar collapsed={collapsed} onToggle={toggle} />
              </ErrorBoundary>
            </aside>
            {/* mobile drawer */}
            {mobileOpen && (
              <div className="fixed inset-0 z-50 lg:hidden print:hidden">
                <div className="absolute inset-0 animate-fade-in bg-slate-950/50" onClick={() => setMobileOpen(false)} />
                <div className="absolute inset-y-0 left-0 w-72 animate-slide-up shadow-pop">
                  <Sidebar collapsed={false} mobile onNavigate={() => setMobileOpen(false)} />
                </div>
              </div>
            )}
            <div className={cx("transition-[padding] duration-200 ease-out print:pl-0", collapsed ? "lg:pl-[72px]" : "lg:pl-64")}>
              <ErrorBoundary resetKey={path}>
                <TopBar onMenu={() => setMobileOpen(true)} onSearch={() => setPalette(true)} />
              </ErrorBoundary>
              <main className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8 print:max-w-none print:p-0">
                <ErrorBoundary resetKey={path}>
                  <Banners />
                </ErrorBoundary>
                <ErrorBoundary resetKey={path}>{children}</ErrorBoundary>
              </main>
            </div>
            <CommandPalette open={palette} onClose={() => setPalette(false)} />
          </div>
        </DriveSyncProvider>
        </Gate>
      </FeedbackProvider>
    </InvoiceStoreProvider>
    </ErrorBoundary>
  );
}
